'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils.js'
import { Button } from './button.js'
import { Input } from './input.js'
import { Textarea } from './textarea.js'

/**
 * `data-disabled="true"` styles group chrome only. Set `disabled` on each
 * InputGroupInput / InputGroupTextarea that should be inactive; the hook does
 * not disable, fade, or prevent focus on descendants. Buttons own their state
 * independently, so an action can remain enabled beside a disabled control.
 */
function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='input-group'
      role='group'
      className={cn(
        // No `outline-none` here: it sets `--tw-outline-style: none` on this
        // element, which then suppresses the focus outline below (which reads
        // `outline-style: var(--tw-outline-style)`).
        'group/input-group relative flex h-12 w-full min-w-0 items-stretch rounded-sm motion-safe:transition-colors',
        // Every colour routes through the --input-* semantic tokens (layer 3,
        // theme.css / the registry theme item) — the same set Input uses, so a
        // group and a bare input are the same control. The role tokens behind
        // them are mode-aware, so there are no dark: variants here.
        'border border-(--input-border) bg-(--input-surface)',
        // Hover — owned by the wrapper so the whole control reacts, addons
        // included, rather than just the inner control's box.
        //
        // Known nuance, deliberately NOT fixed here: hovering an embedded
        // button also paints this surface, so the 464px field greys behind a
        // control the pointer is only 85px inside. Suppressing it with
        // `has-[button:hover]:bg-(--input-surface)` works for the button, but
        // measurably breaks the ordinary field hover on any group holding a
        // button — Chromium stops re-evaluating the plain `:hover` rule on a
        // subject that also carries a `:has(…:hover)` rule, so the field hover
        // silently stopped painting at all. Losing the common interaction to
        // sharpen the rare one is the wrong trade. Revisit if the group ever
        // gains a wrapper that can own hover without `:has()`.
        'hover:bg-(--input-surface-hover)',
        // ...but a disabled group must not react at all. A standalone Input
        // cannot: `disabled:pointer-events-none` means `:hover` never matches
        // it. This wrapper is a plain div, and `pointer-events` on the CONTROL
        // does not stop its ancestor being hovered, so without this line a dead
        // field painted the full enabled hover surface — measured with a real
        // pointer at `--input-surface-hover`, identical to an enabled group.
        // `pointer-events-none` on the wrapper is not the fix: it would also
        // kill an enabled action sitting inside a disabled-field group.
        //
        // Mirrored for `data-disabled`, the manual escape hatch the addon also
        // honours. Every wrapper rule keyed on a disabled control needs its
        // twin so the hook consistently styles the chrome — previously the
        // addon's hairline faded and its cursor changed while this border and
        // hover surface stayed fully live.
        'has-[[data-slot=input-group-control]:disabled]:hover:bg-(--input-surface)',
        'data-[disabled=true]:hover:bg-(--input-surface)',
        // Disabled — the group fades its own CHROME: this border, plus the
        // addon's hairline and cursor. Every child owns its own state, and
        // nothing in this component puts `opacity` on an ancestor.
        //
        // The wrapper-opacity version of this shipped briefly and was wrong:
        // ancestor opacity composites the whole subtree and a child cannot opt
        // out of it, so an ENABLED action beside a disabled field rendered at
        // 50% — measured white-on-navy at 3.12:1, under the 4.5:1 floor, on a
        // button that was still clickable. A control that looks disabled and
        // isn't is worse than the chrome bug it replaced. It also forced
        // `opacity-100` neutralisers onto four children to stop 0.5 x 0.5
        // compounding, and those only worked for natively `:disabled` targets.
        // Fading the chrome directly needs no neutralisers and cannot compound.
        //
        // `not-has-[…aria-invalid…]` is load-bearing, not belt-and-braces. This
        // rule and the aria-invalid border rule below both set `border-color`
        // at identical specificity — `:has()` takes its most specific argument,
        // so `:has([data-slot=input-group-control]:disabled)` (attribute +
        // pseudo-class) and `:has([data-slot][aria-invalid=true])` (two
        // attributes) are both (0,2,0), plus one class each. Without the
        // exclusion only emission order separates them, and this package's
        // classes can be re-emitted after ours by a consuming app's own
        // Tailwind build — the two-build hazard check:cascade exists for, which
        // it cannot catch here because it only flags conditional at-rule pairs.
        // A field that fails validation and is then disabled during submit hits
        // exactly this: the faded grey would erase the error border. Making the
        // two mutually exclusive decides it by MATCHING instead of by order.
        // The `data-disabled` twin below carries the same exclusion, for the
        // same reason: an error border must outlive either disabled path.
        'has-[[data-slot=input-group-control]:disabled]:not-has-[[data-slot][aria-invalid=true]]:border-(--input-border)/50',
        'data-[disabled=true]:not-has-[[data-slot][aria-invalid=true]]:border-(--input-border)/50',
        // Focus — the group draws one outline for whichever control is focused;
        // InputGroupInput / InputGroupTextarea suppress their own. Offset and
        // colour are unconditional because neither renders anything until
        // `outline-2` supplies an outline-style, and keeping them off the
        // has-[] variant leaves the invalid colour below a plain specificity
        // win instead of a source-order tie between two has-[] rules.
        'outline-offset-2 outline-(--input-ring)',
        'has-[[data-slot=input-group-control]:focus-visible]:outline-2',
        // Invalid — 2px danger border, danger hover surface and danger focus
        // outline, mirroring Input's aria-invalid treatment. Each rule carries
        // the has-[] selector, so it outranks its unqualified counterpart on
        // specificity rather than on emission order.
        'has-[[data-slot][aria-invalid=true]]:border-2 has-[[data-slot][aria-invalid=true]]:border-(--input-invalid-border)',
        'has-[[data-slot][aria-invalid=true]]:outline-(--input-invalid-ring)',
        // Excluded when the control is disabled, for the same reason the border
        // rule above is: this and the disabled hover rule both compute to
        // (0,4,0) — class + `:has()` + `:hover` on each — so a tie decided by
        // emission order would let an invalid AND disabled field paint the
        // danger hover surface. Mutually exclusive selectors decide it by
        // MATCHING, and disabled is the one that should win: a dead control
        // gives no hover feedback whatever its validation state. BOTH disabled
        // paths are excluded — the hover rules are (0,3,0) for `data-disabled`
        // against this rule's (0,6,0), so excluding only the control-disabled
        // one would have let an invalid group flagged via the hatch keep
        // painting the danger hover surface.
        'has-[[data-slot][aria-invalid=true]]:not-has-[[data-slot=input-group-control]:disabled]:not-data-[disabled=true]:hover:bg-(--input-invalid-surface-hover)',
        // An InputGroupAction sits flush to the trailing edge with square
        // corners, so the group clips it to its own radius — cheaper and
        // RTL-correct next to per-corner arithmetic on the action. Scoped to
        // groups that actually hold one: a bare `overflow-hidden` would clip
        // DESCENDANT outlines, and InputGroupButton's focus ring sits 2px
        // outside itself, so every Combobox chevron would lose its ring.
        'has-[[data-slot=input-group-action]]:overflow-hidden',
        // Block-aligned addons stack the group and let it grow to fit
        'has-data-[align=block-end]:rounded-sm has-data-[align=block-start]:rounded-sm has-[textarea]:rounded-sm',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col has-[>textarea]:h-auto',
        // Addon-driven padding on the inner input
        'has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3',
        className,
      )}
      {...props}
    />
  )
}

const inputGroupAddonVariants = cva(
  // `--input-placeholder` is the input token set's de-emphasised-text role, and
  // an icon or text affix is exactly that — so addons restyle with the input
  // rather than drifting from it. Mode-aware, hence no dark: variants.
  //
  // Type and glyphs are sized off the control's own `text-base`, not off the
  // box. An affix sits on the same line as the value, so 16px is the floor —
  // the old `text-xs/relaxed` (12px) and 10px kbd were scaled for the 28px
  // group this component used to be and read as fine print beside 16px input
  // text. Glyphs are `size-5` (20px), 1.25x the label, the same reasoning
  // button.tsx documents for a label glyph.
  // An addon holding an InputGroupAction yields its own padding on that edge so
  // the action reaches the group's inner border, and takes a 48px floor so the
  // row still has height once the padding is gone (the affix text centres in
  // it via `items-center`). Each override carries the has-[] selector, so it
  // outranks the base padding on specificity rather than on emission order.
  // Disabled: the addon fades its own HAIRLINE and swaps its cursor, and never
  // touches `opacity`. This is the whole point, so it is worth stating plainly:
  // the addon is an ANCESTOR of every inline button (InputGroupButton always sits
  // in one, and a block-end InputGroupAction does too). `opacity` on this
  // element therefore composites those buttons, and a child cannot opt out of
  // an ancestor's opacity. Measured with `opacity-50` here: an ENABLED button
  // beside a disabled field rendered at effective 0.5 while still clickable,
  // and a disabled one multiplied with Button's own `data-disabled:opacity-50`
  // to 0.25 — which is exactly the Combobox shape, since ComboboxInput passes
  // `disabled` to the control and to the trigger and clear buttons at once.
  // Fading only what this element itself paints leaves every descendant
  // untouched and cannot compound.
  //
  // The affix CONTENT is deliberately not faded — only the hairline is. Two
  // attempts to fade it were both wrong and are worth recording so neither is
  // retried. A faded `color` on the addon never reaches InputGroupText, which
  // sets its own `color` and so stayed lit beside a faded glyph. Expressing the
  // same fade as `opacity` on the children fixed that, but the a11y gate
  // rejects both: axe measures the affix label at 2.43:1 and cannot tell it
  // belongs to a disabled control, because the span carries no disabled state
  // of its own and the addon is a sibling of the control, not its ancestor.
  //
  // Leaving it legible is also the better answer. An affix is an adornment —
  // "AUD", "Markdown supported" — closer to a label than to the control, and a
  // label beside a disabled input does not fade either. The disabled signal is
  // carried by the wrapper's border, this hairline, `cursor-not-allowed`, and
  // the control's own fade, which is four cues without making information
  // harder to read. Fading it would need `aria-disabled` plumbed onto a
  // presentational div to keep axe honest, for a worse result.
  //
  // Background is likewise NOT faded: `--surface-sunken` measures 1.1:1 against
  // the field, so 50% of it is imperceptible, and an `inline-end` addon has no
  // background at all — a base-level `bg` fade would ADD a grey panel.
  //
  // Both conditions get the same paint. `data-disabled` on the group is a
  // manual escape hatch nothing in this component sets, but it had the same
  // ancestor-opacity defect, so it is converted rather than left as a landmine.
  //
  // The affix also advertises a text caret, because clicking it focuses the
  // control. Over a disabled control that is a lie, so it takes the same
  // `cursor-not-allowed` the control itself does.
  "flex cursor-text items-center justify-center gap-1 text-base text-(--input-placeholder) select-none group-has-[[data-slot=input-group-control]:disabled]/input-group:cursor-not-allowed group-has-[[data-slot=input-group-control]:disabled]/input-group:border-(--input-border)/50 group-data-[disabled=true]/input-group:cursor-not-allowed group-data-[disabled=true]/input-group:border-(--input-border)/50 has-[[data-slot=input-group-action]]:min-h-12 has-[[data-slot=input-group-action]]:py-0 **:data-[slot=kbd]:rounded-[calc(var(--radius-sm)-2px)] **:data-[slot=kbd]:bg-(--input-placeholder)/10 **:data-[slot=kbd]:px-1 [&>svg:not([class*='size-'])]:size-5",
  {
    variants: {
      align: {
        // No negative nudge for buttons. `me-[-0.275rem]` was tuned to cancel a
        // 20px `px-1` button in the 28px group this component used to be; at
        // 32px and `px-3` it under-compensates and left the inline button 3.6px
        // off the group's inner edge while the block-end row sat at 8px. Every
        // inset is now that same 8px, so affixes, buttons and footer rows all
        // land on one grid. The kbd nudge stays — a kbd chip is small enough
        // that the optical correction still reads.
        // ONE hairline colour for the whole control: `--input-border`, the same
        // token the group's own edge uses. The mockup drew internal dividers in
        // the lighter `--border-default`, but mixing the two put a #dcdfe0
        // divider beside a #888f92 seam and outer edge, which reads as the
        // junctions being heavier rather than as a hierarchy. It also fixes
        // dark mode: `--border-default` measures 1.12:1 on the dark surface,
        // under the 3:1 floor for a boundary that has to identify a control.
        //
        // The leading affix is a CELL, not floating text: a recessed fill, a
        // hairline on its inner edge, and the group's full height. This is what
        // makes the trailing action read as its mirror — the approved design's
        // words were "the trailing cell mirrors the leading AUD cell exactly".
        // 16px padding matches the value's own, so affix and value sit on one
        // rhythm.
        // Weight lives per align, not in the base: the design sets the inline
        // affix at 600 (`.affix`) but the block row's hint at 300
        // (`.foot .hint`). A base `font-semibold` made "Markdown supported"
        // read as a label at the same weight as the AUD affix.
        //
        // No kbd nudge here any more. `ms-[-0.275rem]` was harmless while this
        // addon was transparent text; now that it paints a fill, a border-inline
        // -end and a rounded start corner, a negative start margin drags the
        // whole cell out past the group's own border.
        'inline-start':
          'order-first rounded-s-[calc(var(--radius-sm)-1px)] border-e border-(--input-border) bg-(--surface-sunken) px-4 font-semibold has-[[data-slot=input-group-action]]:ms-0 has-[[data-slot=input-group-action]]:items-stretch has-[[data-slot=input-group-action]]:p-0',
        'inline-end':
          'order-last px-2 font-semibold has-[[data-slot=input-group-action]]:me-0 has-[[data-slot=input-group-action]]:items-stretch has-[[data-slot=input-group-action]]:p-0 has-[>kbd]:me-[-0.275rem]',
        // Block addons are chrome, not content, so they carry the boundary the
        // Hairline Rule asks for and sit on the recessed surface. Without both
        // the row floats in the writing area with nothing dividing it.
        //
        // `--surface-sunken`, NOT `--background-subtle`: the latter resolves to
        // the same value as `--surface-default` in dark, so the tint vanishes
        // there. Sunken differs from the group's surface in BOTH modes.
        //
        // The corners are rounded rather than clipped by the group: a group-level
        // `overflow-hidden` would cut off the focus ring of any button inside
        // the row, since Button rings 2px outside itself.
        'block-start':
          'order-first min-h-12 w-full justify-start rounded-t-[calc(var(--radius-sm)-1px)] border-b border-(--input-border) bg-(--surface-sunken) px-4 font-light has-[[data-slot=input-group-action]]:items-stretch has-[[data-slot=input-group-action]]:pe-0',
        'block-end':
          'order-last min-h-12 w-full justify-start rounded-b-[calc(var(--radius-sm)-1px)] border-t border-(--input-border) bg-(--surface-sunken) px-4 font-light has-[[data-slot=input-group-action]]:items-stretch has-[[data-slot=input-group-action]]:pe-0',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  },
)

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      role='group'
      data-slot='input-group-addon'
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return
        }
        e.currentTarget.parentElement?.querySelector<HTMLElement>('input, textarea')?.focus()
      }}
      {...props}
    />
  )
}

const inputGroupButtonVariants = cva(
  [
    // No font-size here: Button already sets `text-base/7`, and the
    // `text-xs/relaxed` this used to carry shrank its label to 12px.
    // `before:shadow-none`, NOT `shadow-none`. Button paints its drop shadow on
    // the `::before` fill layer (`before:shadow-sm` in the soft and solid
    // variants), never on the button box — so `shadow-none` was doubly dead:
    // wrong element, and a different tailwind-merge key, so both classes
    // survived and the shadow rendered. Measured: `::before` box-shadow was
    // `rgba(0,0,0,.1) 0 1px 3px`, a cast shadow inside flat hairline chrome.
    'flex items-center gap-2 rounded-sm before:shadow-none',
    // Every size below was inert until this line. Button pins
    // `min-h-(--btn-h)` — 52px at the default step, 60px below `sm` — which
    // outranks a plain `h-*` because they are different properties, so an
    // `xs` button rendered at full size and inflated the row it sat in.
    'min-h-0',
    // Button's padding is a RESPONSIVE pair (`py-[…] sm:py-[…]`), and a media
    // query adds no specificity, so an unprefixed override loses to the `sm:`
    // rule on emission order. Restating both halves (`py-0 sm:py-0`) fixes the
    // render but pairs a bare utility with a conditional one on the same
    // property — check:cascade rejects that, and rightly: a consumer whose own
    // build emits `.py-0` can have their copy land last on our element.
    //
    // `data-size` is always set by InputGroupButton, so an attribute selector
    // is 0,2,0 against the media rule's 0,1,0 and wins at every width, with no
    // second rule to order against.
    'data-[size]:py-0',
    // Same reason as InputGroupAction: zeroing Button's padding exposes its
    // `items-baseline` base, which no longer centres the label.
    'items-center',
    // No disabled-opacity handling here on purpose. Button's own
    // `data-disabled:opacity-50` is the whole treatment: this button always
    // renders inside an addon, and the addon fades its own paint rather than
    // its opacity, so there is no ancestor fade to compound with and nothing
    // to neutralise.
  ],
  {
    variants: {
      // Heights are capped at 32px: the addon pads 8px top and bottom inside a
      // 48px group, so anything taller re-inflates the row it is meant to sit
      // inside. `sm` buys horizontal room rather than height for that reason.
      size: {
        xs: "h-8 gap-1 data-[size=xs]:px-3 [&>svg:not([class*='size-'])]:size-5",
        sm: "h-8 gap-1 data-[size=sm]:px-4 [&>svg:not([class*='size-'])]:size-5",
        'icon-xs': 'size-7 data-[size=icon-xs]:px-0',
        'icon-sm': 'size-8 data-[size=icon-sm]:px-0',
      },
    },
    defaultVariants: {
      size: 'xs',
    },
  },
)

/**
 * The inline button inside an addon — a secondary action or an icon
 * affordance, as opposed to `InputGroupAction`, which attaches to the group's
 * trailing edge.
 *
 * Defaults to `soft` rather than `ghost`: a ghost button inside a control is
 * bare text with no shape, location or formatting to signal it is pressable,
 * which is the one thing an affordance has to do without being hovered. `soft`
 * gives it a 10% ink chip and stays quieter than the attached action beside
 * it. Icon affordances that should stay bare — Combobox's chevron and clear —
 * pass `variant='ghost'` explicitly and are unaffected.
 */
function InputGroupButton({
  className,
  type = 'button',
  variant = 'soft',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset'
  }) {
  return (
    <Button
      type={type}
      data-slot='input-group-button'
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  )
}

const inputGroupActionVariants = cva(
  [
    // Fills the group's height and sits flush against its trailing edge — one
    // block, not a button floating in addon padding. Button pins
    // `min-h-(--btn-h)` (52px at the default step, 60px below `sm`), which a
    // 48px group cannot hold, so `self-stretch` sizes the segment off the group
    // and `min-h-0` clears the floor.
    //
    // `min-h-0`, NOT `[--btn-h:0px]`: the size step publishes its height through
    // a RESPONSIVE pair (`[--btn-h:…] sm:[--btn-h:…]`), and a media query adds
    // no specificity, so an unprefixed custom-property override loses to the
    // `sm:` rule on emission order alone. Overriding the property itself lands
    // in tailwind-merge's `min-h` group and drops Button's utility outright.
    'h-auto min-h-0 self-stretch',
    // Square: the group re-rounds the one outer corner by clipping (see
    // `has-[[data-slot=input-group-action]]:overflow-hidden` on InputGroup), so
    // there is no corner arithmetic here. The `before`/`after` fill layers are
    // squared too — left at `calc(var(--radius-sm)-1px)` they notch the flush
    // edges.
    'rounded-none before:rounded-none after:rounded-none dark:after:rounded-none',
    // Flush against the value: no optical border, and no shadow — a `shadow-sm`
    // whisper belongs under a button sitting on the page, not one inset into a
    // control. Padding is restated for both breakpoints because Button's is a
    // responsive pair (see the min-h note above).
    // Padding is keyed on `data-slot` rather than restated at both breakpoints:
    // Button's own padding is a responsive pair, and a bare-plus-conditional
    // override of the same property is what check:cascade forbids. The
    // attribute selector is 0,2,0 and wins at every width on specificity.
    'border-0 before:shadow-none data-[slot=input-group-action]:px-5 data-[slot=input-group-action]:py-0',
    // The seam colour is owned here, for both modes, in the ALL-SIDES form.
    // Button's ghost/soft/solid each set `dark:border-white/5`, which is a
    // border-color shorthand — an inline-start longhand would only beat it by
    // emission order, and white at 5% measures 1.12:1 on the dark surface, so
    // the seam vanished there. Matching the shorthand puts both in one
    // tailwind-merge group and drops Button's outright. Width stays 0 except
    // where a variant below asks for it, so only that one edge paints.
    'border-(--input-border) dark:border-(--input-border)',
    // Focus draws INSIDE the segment: Button's default `outline-offset-2` puts
    // the ring on the page, which the clipping group cuts off.
    // `items-center`, because Button's base is `items-baseline`. Baseline
    // alignment only looks centred while Button's own vertical padding
    // balances the line box; with `py-0` and a stretched box it parked the
    // label 9px above centre in a 46px segment. Measured, not eyeballed.
    // The focus ring is stated in full here rather than inherited. Button
    // publishes it as four classes (`focus:outline`, `focus:outline-2`,
    // `focus:outline-offset-2`, `focus:outline-(--btn-bg)`); overriding only the
    // offset left the rendered ring at the browser default — measured
    // `solid 1.5px offset 0px`, not the designed 2px inset. Restating width and
    // offset makes the treatment independent of what tailwind-merge does to
    // Button's copies.
    //
    // Colour is `--input-ring`, the token the GROUP rings itself with, so a
    // focused segment matches the control it sits in instead of borrowing the
    // button system's brand ink. iterate-1.css:
    // `.it .act:focus-visible { outline: 2px solid var(--input-ring); outline-offset: -2px }`.
    'items-center font-semibold focus:outline-2 focus:-outline-offset-2 focus:outline-(--input-ring)',
    // Button's own `data-disabled:opacity-50` is left as the entire disabled
    // treatment, for both placements: flush against the group's edge, and
    // nested in a block-end addon. Neither ancestor fades its opacity, so an
    // Apply that waits for a valid value dims on its own while the live field
    // beside it stays at full strength.
  ],
  {
    variants: {
      // Three fills, mapped onto Button's own variants so hover, active and
      // dark-mode handling come from the button system rather than being
      // restated here. `open` and `subtle` have little or no fill contrast
      // against the input surface, so both carry a leading hairline to read as
      // a distinct cell; `solid` needs one only in dark (see below).
      variant: {
        // `open` and `subtle` have no usable fill contrast against the input
        // surface (1:1 and 1.69:1 measured in dark), so both are bounded by a
        // leading hairline in every mode. Colour comes from the base above.
        // The seam is keyed on `data-variant-action` (stamped below) so it beats
        // `border-0` on SPECIFICITY (0,2,0 vs 0,1,0). As a bare `border-s` it
        // did not: tailwind-merge keeps BOTH `border-0` and `border-s` (verified
        // — `border-w-s` invalidates nothing), `border-width:0` expands to the
        // physical longhands and collides with `border-inline-start-width` at
        // equal specificity, so only emission order separated them. Ours wins
        // today by 199 bytes in dist/styles.css; a consuming app that emits its
        // own `.border-0` after ours would reset the seam to 0 and `open` would
        // lose the only thing delineating it. check:cascade cannot catch this —
        // it only flags conditional at-rule pairs.
        open: 'bg-transparent data-[variant-action=open]:border-s',
        // `--btn-bg` (the button's own ink) for the ring, NOT the group's
        // `--input-ring`. The base picks `--input-ring` so a focused segment
        // matches the control it sits in, which holds for `open` — measured
        // 3.29:1 against the field surface showing through. On `subtle` the
        // ring lands on the tinted fill instead and measures 2.70:1, under the
        // 3:1 non-text floor of WCAG 1.4.11. The ink reads 14.37:1 there and
        // satisfies the Derived State Rule, which wants focus derived from the
        // one ink variable rather than independently chosen.
        subtle: 'focus:outline-(--btn-bg) data-[variant-action=subtle]:border-s',
        // Button rings in `--btn-bg`, which for a solid button IS the fill, so
        // the inset ring above would be navy-on-navy. `--btn-text` is the label
        // colour: 14.37:1 on the fill in both modes.
        //
        // The dark seam is not decoration. Button's `primary` reads the
        // masterbrand ramp (--color-primary-800, Blue 01), which is
        // theme-invariant, so on the dark group surface the fill measures
        // 1.32:1 — under the 3:1 floor for identifying a UI component. The
        // other three edges are bounded by the group's own border; this
        // continues that hairline inward. Light needs none at 15.26:1, and a
        // grey seam there would read as a gap.
        solid: 'focus:outline-(--btn-text) data-[variant-action=solid]:dark:border-s',
      },
    },
    defaultVariants: {
      variant: 'solid',
    },
  },
)

/** Maps this component's fill names onto Button's variant names. */
const inputGroupActionButtonVariant = {
  open: 'ghost',
  subtle: 'soft',
  solid: 'solid',
} as const

function InputGroupAction({
  className,
  type = 'button',
  variant = 'solid',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type' | 'variant'> &
  VariantProps<typeof inputGroupActionVariants> & {
    type?: 'button' | 'submit' | 'reset'
  }) {
  return (
    <Button
      type={type}
      data-slot='input-group-action'
      data-variant-action={variant}
      variant={inputGroupActionButtonVariant[variant ?? 'solid']}
      className={cn(inputGroupActionVariants({ variant }), className)}
      {...props}
    />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        // Matches the addon it sits in: 16px type, `--input-placeholder` ink,
        // and a nested glyph the same size as one beside it.
        //
        // No disabled rule here, and none on the addon either: affix content
        // stays at full contrast in a disabled group. Do not add one. Two
        // attempts are recorded on inputGroupAddonVariants — a faded `color` on
        // the addon never reaches this span, because setting `color` here
        // overrides the inherited one for its own subtree; and expressing the
        // same fade as `opacity` on the addon's children fixed that but failed
        // the a11y gate, which measures this label at 2.43:1 and cannot tell it
        // belongs to a disabled control. The disabled signal lives on chrome.
        "flex items-center gap-2 text-base text-(--input-placeholder) [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5",
        className,
      )}
      {...props}
    />
  )
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <Input
      data-slot='input-group-control'
      className={cn(
        // The group owns the box, so fill its height (Input is `h-12` on its
        // own) and drop the border, surface and radius to let the group's
        // chrome show through.
        //
        // The hover surfaces must be neutralised too, even though the group
        // paints the identical token: this control is `rounded-none` and
        // `flex-1`, so with no inline-end addon its right edge lands 1px
        // inside the group's border and its SQUARE corner fills the group's
        // `rounded-sm` corner notch. Same colour, wrong shape — the group's
        // own paint is clipped to its radius, the control's is not. Letting
        // only the group paint keeps the corners round in both the default
        // and the aria-invalid state (where the notch is 2px, not 1px).
        //
        // `outline-0` / `border-0` rather than `outline-none` / `border-none`:
        // tailwind-merge treats those as the same group as Input's
        // `focus-visible:outline-2` / `aria-invalid:border-2` and drops them
        // from the class string outright, so there is never a pair of
        // same-specificity rules whose winner depends on stylesheet order.
        // Input's own `disabled:opacity-50` is deliberately left intact: the
        // group fades only its own chrome, so this control owns its disabled
        // appearance exactly as a bare Input does, and nothing compounds.
        'h-full flex-1 rounded-none border-0 bg-transparent hover:bg-transparent focus-visible:outline-0 aria-invalid:border-0 aria-invalid:hover:bg-transparent',
        className,
      )}
      {...props}
    />
  )
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      data-slot='input-group-control'
      className={cn(
        // Identical contract to InputGroupInput, because Textarea is now
        // Input's twin: same `--input-*` tokens, same outline focus treatment,
        // same `aria-invalid:border-2`. The group owns all three, so the same
        // three neutralisers apply, for the same tailwind-merge reason
        // (`outline-0` / `border-0` land in Button's own conflict groups and
        // drop its utilities from the class string rather than racing them).
        // Textarea's own `disabled:opacity-50` is left intact, as in
        // InputGroupInput — the group fades only its chrome.
        //
        // The two `hover:bg-transparent` neutralisers are here for the reason
        // InputGroupInput documents above, and were missing: `bg-transparent`
        // and `hover:bg-*` are different tailwind-merge keys, so Textarea's own
        // `hover:bg-(--input-surface-hover)` survived on the element and
        // outranks the flat `bg-transparent` on specificity. This box is
        // `rounded-none` inside the group's `rounded-sm`, and the group only
        // clips when it holds an action — so a hover surface painted by the
        // control itself can square off the group's corner notch, which is the
        // artefact the input's copies exist to prevent. Restores the "identical
        // contract to InputGroupInput" this comment claims.
        'flex-1 rounded-none border-0 bg-transparent hover:bg-transparent focus-visible:outline-0 aria-invalid:border-0 aria-invalid:hover:bg-transparent',
        // Only the vertical padding differs from Textarea's own: the design's
        // writing surface is `12px 16px`, and Textarea ships `py-2`. Its
        // `min-h-24` (96px) and `px-4` already match the design, so restating
        // them here would be dead weight.
        'py-3',
        className,
      )}
      {...props}
    />
  )
}

export {
  InputGroup,
  InputGroupAction,
  inputGroupActionVariants,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
}
